import type { Disposable } from '@blocksuite/global/disposable';
import {
  BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE,
  type BlobEngine,
  type BlobState,
} from '@blocksuite/sync';
import {
  computed,
  effect,
  type ReadonlySignal,
  signal,
} from '@preact/signals-core';
import type { TemplateResult } from 'lit-html';

export type ResourceKind = 'Blob' | 'File' | 'Image';

/**
 * From this size on, attachments load through the stream URL instead of a
 * complete local copy, so they open and download without a full fetch first.
 */
export const STREAM_URL_MIN_BYTES = 32 * 1024 * 1024;

function isMediaMime(mime: string | undefined): boolean {
  return !!mime && (mime.startsWith('video/') || mime.startsWith('audio/'));
}

function releaseObjectUrl(url: string): void {
  // Stream URLs point at the server; only object URLs hold local resources.
  if (url.startsWith('blob:')) URL.revokeObjectURL(url);
}

export type StateKind =
  | 'loading'
  | 'uploading'
  | 'error'
  | 'error:oversize'
  | 'none';

export type StateInfo = {
  icon: TemplateResult;
  title?: string;
  description?: string | null;
};

export type ResolvedStateInfoPart = {
  loading: boolean;
  error: boolean;
  errorCode?: BlobState['errorCode'] | null;
  state: StateKind;
  url: string | null;
  needUpload: boolean;
};

export type ResolvedStateInfo = StateInfo & ResolvedStateInfoPart;

export class ResourceController implements Disposable {
  readonly blobUrl$ = signal<string | null>(null);

  // TODO(@fundon): default `loading` status.
  readonly state$ = signal<Partial<BlobState>>({});

  readonly resolvedState$ = computed<ResolvedStateInfoPart>(() => {
    const url = this.blobUrl$.value;
    const {
      needUpload = false,
      uploading = false,
      downloading = false,
      overSize = false,
      errorMessage,
      errorCode,
    } = this.state$.value;
    const hasExceeded = overSize;
    const hasError = hasExceeded || Boolean(errorMessage);
    const state = this.determineState(
      hasExceeded,
      hasError,
      uploading,
      downloading
    );

    const loading = state === 'uploading' || state === 'loading';

    return {
      error: hasError,
      errorCode,
      needUpload,
      loading,
      state,
      url,
    };
  });

  private engine?: BlobEngine;

  private revision = 0;

  private disposed = false;

  private subscribedBlobId: string | undefined;

  constructor(
    readonly blobId$: ReadonlySignal<string | undefined>,
    readonly kind: ResourceKind = 'File'
  ) {}

  // This is a tradeoff, initializing `Blob Sync Engine`.
  setEngine(engine: BlobEngine) {
    this.engine = engine;
    this.disposed = false;
    this.revision++;
    return this;
  }

  determineState(
    hasExceeded: boolean,
    hasError: boolean,
    uploading: boolean,
    downloading: boolean
  ): StateKind {
    if (hasExceeded) return 'error:oversize';
    if (hasError) return 'error';
    if (uploading) return 'uploading';
    if (downloading) return 'loading';
    return 'none';
  }

  resolveStateWith(
    info: {
      loadingIcon: TemplateResult;
      errorIcon?: TemplateResult;
    } & StateInfo
  ): ResolvedStateInfo {
    const { error, errorCode, loading, state, url, needUpload } =
      this.resolvedState$.value;

    const { icon, title, description, loadingIcon, errorIcon } = info;

    const result = {
      error,
      errorCode,
      loading,
      state,
      icon,
      title,
      description,
      url,
      needUpload,
    };

    if (loading) {
      result.icon = loadingIcon ?? icon;
    } else if (error) {
      result.icon = errorIcon ?? icon;
      result.description = this.state$.value.errorMessage ?? description;
    }

    return result;
  }

  updateState(state: Partial<BlobState>) {
    this.state$.value = { ...this.state$.value, ...state };
  }

  subscribe() {
    return effect(() => {
      const blobId = this.blobId$.value;
      if (blobId !== this.subscribedBlobId) {
        this.subscribedBlobId = blobId;
        this.revision++;
        this.state$.value = {};
        this.releaseUrl();
      }
      const revision = this.revision;
      if (!blobId) return;

      const blobState$ = this.engine?.blobState$(blobId);
      if (!blobState$) return;

      const subscription = blobState$.subscribe(state => {
        if (this.disposed || revision !== this.revision) return;
        let { uploading, downloading, errorCode, errorMessage } = state;
        if (state.overSize) {
          uploading = false;
          downloading = false;
        } else if ((uploading || downloading) && errorMessage) {
          errorCode = null;
          errorMessage = null;
        }

        this.updateState({
          ...state,
          uploading,
          downloading,
          errorCode,
          errorMessage,
        });
      });

      return () => subscription.unsubscribe();
    });
  }

  async blob() {
    const blobId = this.blobId$.peek();
    if (!blobId || this.disposed) return null;
    const revision = this.revision;

    let blob: Blob | null = null;
    let errorCode: BlobState['errorCode'] | null = null;
    let errorMessage: string | null = null;

    try {
      if (!this.engine) {
        throw new Error('Blob engine is not initialized');
      }

      blob = (await this.engine.get(blobId)) ?? null;

      if (!blob) errorMessage = `${this.kind} not found`;
    } catch (err) {
      console.error(err);
      if (isLocalStorageUnavailableError(err)) {
        errorMessage = err.message;
        errorCode = BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE;
      } else {
        errorMessage = `Failed to retrieve ${this.kind}`;
      }
    }

    if (
      this.disposed ||
      revision !== this.revision ||
      blobId !== this.blobId$.peek()
    ) {
      return null;
    }

    if (errorMessage) this.updateState({ errorCode, errorMessage });

    return blob;
  }

  async createUrlWith(type?: string) {
    let blob = await this.blob();
    if (!blob) return null;

    if (type) blob = new Blob([blob], { type });

    return URL.createObjectURL(blob);
  }

  async refreshUrlWith(type?: string) {
    const revision = this.revision;
    // Resets the state.
    this.state$.value = {};

    const url =
      (await this.streamUrlFor(type)) ?? (await this.createUrlWith(type));
    if (!url) return;

    if (this.disposed || revision !== this.revision) {
      releaseObjectUrl(url);
      return;
    }

    this.replaceUrl(url);
  }

  /** Stream URL for media and large files; null keeps the blob path. */
  private async streamUrlFor(type?: string): Promise<string | null> {
    const blobId = this.blobId$.peek();
    if (!blobId || !this.engine) return null;
    // Metadata failures surface through the regular blob read that follows.
    const metadata = await this.engine.getMetadata(blobId).catch(() => null);
    const streamable =
      isMediaMime(type) ||
      isMediaMime(metadata?.mime) ||
      (metadata?.size ?? 0) >= STREAM_URL_MIN_BYTES;
    if (!streamable) return null;
    return this.engine.streamUrl(blobId);
  }

  /** Reuse a retrieved/converted blob without another storage read. */
  refreshUrlFromBlob(blob: Blob, expectedBlobId: string | undefined) {
    if (this.disposed || expectedBlobId !== this.blobId$.peek()) return;
    this.state$.value = {};
    this.replaceUrl(URL.createObjectURL(blob));
  }

  private replaceUrl(url: string) {
    const prevUrl = this.blobUrl$.peek();

    this.blobUrl$.value = url;

    if (!prevUrl) return;

    releaseObjectUrl(prevUrl);
  }

  // Re-upload to the server.
  async upload() {
    const blobId = this.blobId$.peek();
    if (!blobId) return;

    const state = this.state$.peek();
    if (!state.needUpload) return;
    if (state.uploading) return;

    // Resets the state.
    this.state$.value = {};

    return await this.engine?.upload(blobId);
  }

  dispose() {
    this.disposed = true;
    this.revision++;
    this.releaseUrl();
  }

  private releaseUrl() {
    const url = this.blobUrl$.peek();
    this.blobUrl$.value = null;
    if (!url) return;

    releaseObjectUrl(url);
  }
}

function isLocalStorageUnavailableError(error: unknown): error is Error {
  return (
    error instanceof Error &&
    (error.name === 'StreamLainBlobLocalStorageUnavailableError' ||
      (error as { code?: unknown }).code ===
        BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE)
  );
}
